import React from 'react';
import { useTranslation } from 'react-i18next';
import { QRCodeSVG } from 'qrcode.react';
import { Shield } from 'lucide-react';
import { MERGE_FIELDS, type CertState, type CertTemplate } from './certificateConfig';

interface CertificatePreviewA4Props {
  template: CertTemplate;
  state: CertState;
  /** Placeholder verification URL for the QR (no backend verification exists yet). */
  verifyUrl: string;
  /** Real context filled in at design time — the org and test are already known. */
  orgName: string;
  testTitle: string;
  instructorName?: string;
}

const Token: React.FC<{ children: React.ReactNode; size?: 'sm' | 'lg' }> = ({ children, size = 'sm' }) => (
  <span
    className={
      size === 'lg'
        ? 'inline-flex items-center rounded px-2 py-0.5 font-mono text-xl font-bold'
        : 'inline-flex items-center rounded px-1.5 py-0.5 font-mono text-[11px] font-medium'
    }
    style={{ backgroundColor: '#EFF6FF', color: '#1D4ED8' }}
  >
    {children}
  </span>
);

/** A design mock — the real per-attempt PDF is generated server-side. Always white, so it ignores app theme. */
export const CertificatePreviewA4: React.FC<CertificatePreviewA4Props> = ({
  template,
  state,
  verifyUrl,
  orgName,
  testTitle,
  instructorName,
}) => {
  const { t } = useTranslation();
  const tokenFor = (id: string) => MERGE_FIELDS.find((f) => f.id === id)?.token ?? `[${id}]`;
  const show = (id: string) => Boolean(state.fields[id]);

  return (
    <div className="w-full overflow-x-auto">
      <div
        className="relative mx-auto flex aspect-[7/5] w-full min-w-[440px] max-w-[560px] flex-col items-center justify-center bg-white px-12 py-8 text-center"
        style={{ border: `3px solid ${template.outer}` }}
      >
        <div
          className="pointer-events-none absolute inset-2 rounded-sm"
          style={{ border: `1px solid ${template.inner}` }}
          aria-hidden
        />

        {/* Org lockup */}
        <div className="relative flex items-center gap-2">
          <span
            className="flex h-7 w-7 items-center justify-center rounded-md"
            style={{ backgroundColor: template.outer }}
          >
            <Shield className="h-3.5 w-3.5 text-white" />
          </span>
          <span className="text-base font-bold text-gray-900">{orgName || t('cert_placeholder_logo')}</span>
        </div>

        <p
          className="relative mt-4 text-[12px] font-semibold uppercase tracking-[0.28em]"
          style={{ color: template.accentText }}
        >
          {t('cert_preview_title')}
        </p>

        <p className="relative mt-4 text-[11px] text-gray-500">{t('cert_certifies_that')}</p>
        <div className="relative mt-1.5">
          {show('student_name') ? (
            <Token size="lg">{tokenFor('student_name')}</Token>
          ) : (
            <span className="text-xl font-bold text-gray-300">—</span>
          )}
        </div>

        {show('test_name') && (
          <p className="relative mt-3 max-w-[85%] text-[12px] text-gray-600">
            {t('cert_has_completed')}{' '}
            <span className="font-semibold text-gray-900">{testTitle || t('cert_placeholder_test_name')}</span>
          </p>
        )}
        {show('score') && (
          <p className="relative mt-1 text-[12px] text-gray-600">
            {t('cert_with_score_of')} <Token>{tokenFor('score')}</Token>
          </p>
        )}

        {(show('group') || show('cert_id')) && (
          <div className="relative mt-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-[10px] text-gray-500">
            {show('group') && (
              <span>
                {t('cert_field_group')}: <Token>{tokenFor('group')}</Token>
              </span>
            )}
            {show('cert_id') && (
              <span>
                {t('cert_field_cert_id')}: <Token>{tokenFor('cert_id')}</Token>
              </span>
            )}
          </div>
        )}

        {/* Signature line */}
        <div className="relative mt-8 flex w-full max-w-[80%] items-end justify-between gap-8">
          <div className="flex-1 border-t border-gray-300 pt-1 text-[10px] uppercase tracking-wide text-gray-400">
            {show('date') && <Token>{tokenFor('date')}</Token>}
            <span className="mt-0.5 block">{t('cert_field_date')}</span>
          </div>
          <div className="flex-1 border-t border-gray-300 pt-1 text-[10px] uppercase tracking-wide text-gray-400">
            {instructorName && <span className="font-medium normal-case text-gray-700">{instructorName}</span>}
            <span className="mt-0.5 block">{t('cert_instructor')}</span>
          </div>
        </div>

        {state.qrEnabled && (
          <div className="absolute bottom-4 right-5 flex flex-col items-center gap-0.5">
            <QRCodeSVG value={verifyUrl} size={40} />
          </div>
        )}
      </div>
    </div>
  );
};

export default CertificatePreviewA4;
